import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { RefObject } from "react"
import { useQuery } from "@tanstack/react-query"
import { useSnackbar } from "notistack"

import { chatAxios } from "../../../utils/aiService"
import useAuth from "../../../utils/auth"
import type { ChatModelOption, ModelId, SessionInfo } from "../types"
import { usePersistedChatModel } from "../usePersistedChatModel"

export const useChatModels = () => {
  const { isAuthenticated } = useAuth()
  return useQuery<ChatModelOption[]>({
    queryKey: ["chat", "models"],
    queryFn: async () => {
      const response = await chatAxios.get<ChatModelOption[]>("/api/models")
      if (response.status !== 200)
        throw new Error("Failed to load available models")
      const data = response.data satisfies ChatModelOption[]
      if (data.length === 0) throw new Error("No chat models are available")
      return data
    },
    enabled: isAuthenticated,
    staleTime: Infinity,
  })
}

export interface ChatModelStateHook {
  availableModels: ChatModelOption[]
  isLoadingModels: boolean
  hasModelsError: boolean
  retryModels: () => void
  selectedModel: ModelId | null
  /** Validated setter — also updates the active session's model. */
  setModel: (model: ModelId) => void
  defaultModel: ModelId | null
  setDefaultModel: (model: ModelId) => void
  resolveAvailableModelId: (
    preferredModel: ModelId | null | undefined
  ) => ModelId | null
  /** Bind a raw model id to a specific session (e.g. on session creation). */
  setSessionModelValue: (id: string, model: ModelId) => void
  /** Reset selectedModel back to the current defaultModel. */
  resetModel: () => void
  /** Remove a deleted session's model binding. */
  clearSessionModel: (id: string) => void
}

export const useChatModelState = (
  sessionId: string | null,
  fetchedSessions: SessionInfo[],
  activeSessionRef: RefObject<string | null>
): ChatModelStateHook => {
  const { enqueueSnackbar } = useSnackbar()

  const [persistedDefaultModel, setPersistedDefaultModel] =
    usePersistedChatModel()
  const [sessionModels, setSessionModels] = useState<Record<string, ModelId>>(
    {}
  )
  const [newChatModel, setNewChatModel] = useState<ModelId | null>(null)
  const stalePersistedModelNoticeRef = useRef<string>(null)

  const availableModelsQuery = useChatModels()

  const availableModels = useMemo(
    () => availableModelsQuery.data ?? [],
    [availableModelsQuery.data]
  )
  const { refetch: refetchModels } = availableModelsQuery
  const isLoadingModels = availableModelsQuery.isLoading
  const hasModelsError = availableModelsQuery.isError
  const retryModels = useCallback(() => {
    void refetchModels()
  }, [refetchModels])

  const modelExists = useCallback(
    (modelId: ModelId | null | undefined): modelId is ModelId =>
      modelId != null && availableModels.some(model => model.id === modelId),
    [availableModels]
  )

  const resolveAvailableModelId = useCallback(
    (preferredModel: ModelId | null | undefined): ModelId | null => {
      if (modelExists(preferredModel)) {
        return preferredModel
      }
      return availableModels[0]?.id ?? null
    },
    [availableModels, modelExists]
  )

  const defaultModel = useMemo(
    () => resolveAvailableModelId(persistedDefaultModel),
    [persistedDefaultModel, resolveAvailableModelId]
  )

  const resolveSessionModel = useCallback(
    (targetSessionId: string | null) => {
      if (!targetSessionId) {
        return resolveAvailableModelId(
          newChatModel ?? persistedDefaultModel ?? null
        )
      }
      const sessionInfo = fetchedSessions.find(s => s.id === targetSessionId)
      return resolveAvailableModelId(
        sessionModels[targetSessionId] ??
          sessionInfo?.model ??
          persistedDefaultModel ??
          null
      )
    },
    [
      fetchedSessions,
      newChatModel,
      persistedDefaultModel,
      resolveAvailableModelId,
      sessionModels,
    ]
  )

  const selectedModel = useMemo(
    () => resolveSessionModel(sessionId),
    [resolveSessionModel, sessionId]
  )

  // Binds a model to a real session id (called on session creation).
  // Clears any pending new-chat selection so it doesn't linger.
  const setSessionModelValue = useCallback((id: string, model: ModelId) => {
    setSessionModels(prev => ({ ...prev, [id]: model }))
    setNewChatModel(null)
  }, [])

  const clearSessionModel = useCallback((id: string) => {
    setSessionModels(prev => {
      const { [id]: _, ...rest } = prev
      return rest
    })
  }, [])

  const setDefaultModel = useCallback(
    (model: ModelId) => {
      if (!modelExists(model)) return
      void setPersistedDefaultModel(model)
    },
    [modelExists, setPersistedDefaultModel]
  )

  const setModel = useCallback(
    (model: ModelId) => {
      if (!modelExists(model)) return
      const activeId = activeSessionRef.current
      if (activeId) {
        setSessionModels(prev => ({ ...prev, [activeId]: model }))
      } else {
        setNewChatModel(model)
      }
    },
    [activeSessionRef, modelExists]
  )

  const resetModel = useCallback(() => {
    if (defaultModel == null) return
    setModel(defaultModel)
  }, [defaultModel, setModel])

  // Stale persisted model fallback — auto-switch and notify the user.
  useEffect(() => {
    if (isLoadingModels || hasModelsError) return

    const fallbackModel = availableModels[0]?.id ?? null
    if (fallbackModel == null) return

    if (persistedDefaultModel == null) {
      void setPersistedDefaultModel(fallbackModel)
      return
    }

    if (!modelExists(persistedDefaultModel)) {
      void setPersistedDefaultModel(fallbackModel)

      if (stalePersistedModelNoticeRef.current !== persistedDefaultModel) {
        stalePersistedModelNoticeRef.current = persistedDefaultModel
        const fallbackLabel =
          availableModels.find(model => model.id === fallbackModel)?.label ??
          fallbackModel
        enqueueSnackbar(
          `Previously selected model is no longer available. Switched to ${fallbackLabel}.`,
          { variant: "info" }
        )
      }
    }
  }, [
    availableModels,
    enqueueSnackbar,
    hasModelsError,
    isLoadingModels,
    modelExists,
    persistedDefaultModel,
    setPersistedDefaultModel,
  ])

  return {
    availableModels,
    isLoadingModels,
    hasModelsError,
    retryModels,
    selectedModel,
    setModel,
    defaultModel,
    setDefaultModel,
    resolveAvailableModelId,
    setSessionModelValue,
    resetModel,
    clearSessionModel,
  }
}
